import React, { useCallback, useEffect, useState } from "react";
import baseUrl from "../../../../api/baseUrl";
import { useNavigate } from "react-router-dom";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import PersonIcon from "@mui/icons-material/Person";
import socket from "../../../../api/socket";
import AppointmentDetailsModal from "./AppointmentDetailsModal";
import { statusStyles } from "./appointmentViewShared";
import PatientViewModal from "../../../patient/components/PatientViewModal";

const DENTIST_STATUS_BUTTONS = [
  { value: "waiting", label: "On hold" },
  { value: "ongoing", label: "Ongoing" },
  { value: "completed", label: "Completed" },
] as const;

function AppointmentTr({
  item,
  appointmStatus,
  showAddTreatmentButton = true,
  showPatientDetailsButton = false,
  statusAsButtons = false,
  showQueueActions = true,
}: any) {
  const [select, setselect] = useState(item?.status);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const navigate = useNavigate();
  const [patientModalOpen, setPatientModalOpen] = useState(false);
  const [patientLoading, setPatientLoading] = useState(false);
  const [patientData, setPatientData] = useState<any>(null);
  const [theme, setTheme] = useState<"light" | "dark">("light");

  useEffect(() => {
    setselect(item?.status);
  }, [item]);

  useEffect(() => {
    const htmlTheme = document.documentElement.getAttribute("data-theme") as "light" | "dark";
    const savedTheme = (localStorage.getItem("theme") as "light" | "dark") || htmlTheme;
    if (savedTheme) setTheme(savedTheme);
  }, []);

  const applyStatus = useCallback(
    (status: string) => {
      if (!status || status === select) return;
      setselect(status);
      socket.emit("allAppointmentStatus", {
        appointmentid: item?._id,
        status,
        queue: item?.queue,
        roomNumber: item?.roomNumber,
      });
      if (status === "ongoing" && item?.queue != null && item?.queue !== "") {
        try {
          localStorage.setItem(
            "nextQueue",
            String(Math.max(0, Math.floor(Number(item.queue))))
          );
        } catch {
          /* ignore quota / private mode */
        }
      }
      baseUrl
        .patch(
          "/appointment/statusAppointmentById",
          {},
          {
            headers: { appointmentid: item?._id, status },
          }
        )
        .then(() => appointmStatus())
        .catch((error) => console.log(error));
    },
    [appointmStatus, item, select]
  );

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    applyStatus(e.target.value);
  };

  const handleTreatmentClick = useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      baseUrl
        .post("treatment/create-treatment", { patientId: id })
        .then((response) => {
          navigate(
            `/client/treatment-diagnose/add-treatment/diagnose/${response.data.data._id}`
          );
        })
        .catch((error) => console.error(error));
    },
    [navigate]
  );

  const handleViewPatient = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      const pid = item?.patientId?._id;
      if (!pid) return;
      setPatientModalOpen(true);
      setPatientLoading(true);
      try {
        const res = await baseUrl.get("patient/findById", {
          headers: { patientid: pid },
        });
        setPatientData(res.data?.data ?? null);
      } catch (err) {
        console.error(err);
        setPatientData(null);
      } finally {
        setPatientLoading(false);
      }
    },
    [item?.patientId?._id]
  );

  const closePatientModal = useCallback(() => {
    setPatientModalOpen(false);
    setPatientData(null);
  }, []);

  const canUpdateQueue =
    String(item?.sessionName ?? "").trim() !== "" &&
    String(item?.roomNumber ?? "").trim() !== "";

  const handleUpdateQueue = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!canUpdateQueue || !item?._id) return;

      const current =
        item?.queue != null && item?.queue !== "" ? String(item.queue) : "";
      const nextRaw = window.prompt("Enter token number", current);
      if (nextRaw == null) return;
      const n = parseInt(String(nextRaw).trim(), 10);
      if (!Number.isFinite(n) || n < 0) return;

      try {
        await baseUrl.patch(
          "/appointment/queueAppointmentById",
          { queue: n },
          { headers: { appointmentid: item._id } }
        );
      } catch (err) {
        console.error(err);
      }

      socket.emit("allAppointmentStatus", {
        appointmentid: item._id,
        status: "ongoing",
        queue: n,
        roomNumber: item?.roomNumber,
      });
      try {
        localStorage.setItem("nextQueue", String(Math.max(0, Math.floor(Number(n)))));
      } catch {
        /* ignore */
      }

      appointmStatus?.();
    },
    [appointmStatus, canUpdateQueue, item]
  );

  const handleGenerateQueue = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!canUpdateQueue || !item?._id) return;
      try {
        const res = await baseUrl.patch(
          "/appointment/generateQueueAppointmentById",
          {},
          { headers: { appointmentid: item._id } }
        );
        const q = res?.data?.data?.queue;
        if (q == null) return;

        socket.emit("allAppointmentStatus", {
          appointmentid: item._id,
          status: "ongoing",
          queue: q,
          roomNumber: item?.roomNumber,
        });
        try {
          localStorage.setItem("nextQueue", String(Math.max(0, Math.floor(Number(q)))));
        } catch {
          /* ignore */
        }
        appointmStatus?.();
      } catch (err) {
        console.error(err);
      }
    },
    [appointmStatus, canUpdateQueue, item]
  );

  return (
    <tr className="hover:bg-indigo-50/40 dark:hover:bg-indigo-950/20 transition-colors group">
      <td className="px-4 xl:px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">
        {item?.patientId?.fullName ? item?.patientId?.fullName : item?.bookPatientId?.fullName || "N/A"}
      </td>
      <td className="px-4 xl:px-6 py-3 text-sm text-slate-700 dark:text-slate-200">
        {[item?.doctorId?.firstName, item?.doctorId?.lastName].filter(Boolean).join(" ").trim() || "—"}
      </td>
      <td className="px-4 xl:px-6 py-3 whitespace-nowrap">
        <div className="text-sm text-slate-600 dark:text-slate-300">
          {item?.date?.split("T")[0]}
        </div>
        <span className="mt-1 inline-block px-2.5 py-1 bg-primary/10 dark:bg-primary/30 text-primary dark:text-sky-300 rounded-md text-xs font-medium">
          {item?.startTime} - {item?.endTime}
        </span>
      </td>
      <td className="px-4 xl:px-6 py-3 whitespace-nowrap">
        <div className="text-sm font-medium text-slate-800 dark:text-slate-100">
          {item?.sessionName || "—"}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-600 dark:text-slate-300">
          <span>
            Room {String(item?.roomNumber ?? "").trim() || "—"}
          </span>
          <span className="text-slate-300 dark:text-slate-600">·</span>
          <span className="tabular-nums">
            Token{" "}
            {item?.queue != null && item?.queue !== ""
              ? Number(item.queue)
              : "—"}
          </span>
          {showQueueActions && canUpdateQueue ? (
            <>
              <span className="text-slate-300 dark:text-slate-600">·</span>
              <button
                type="button"
                onClick={handleUpdateQueue}
                className="underline underline-offset-2 text-primary hover:text-primary/80"
                title="Update token number"
              >
                Update token
              </button>
              <span className="text-slate-300 dark:text-slate-600">·</span>
              <button
                type="button"
                onClick={handleGenerateQueue}
                className="underline underline-offset-2 text-emerald-700 hover:text-emerald-700/80 dark:text-emerald-300 dark:hover:text-emerald-200"
                title="Generate next token number"
              >
                Generate token
              </button>
            </>
          ) : null}
        </div>
      </td>
      <td className="px-4 xl:px-6 py-3">
        {statusAsButtons ? (
          <div className="flex flex-wrap gap-1.5">
            {DENTIST_STATUS_BUTTONS.map(({ value, label }) => {
              const active = select === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => applyStatus(value)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                    active
                      ? statusStyles[value] || statusStyles.default
                      : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500"
                  }`}
                  aria-pressed={active}
                >
                  {label}
                </button>
              );
            })}
          </div>
        ) : (
          <select
            onChange={handleChange}
            value={select}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors ${
              statusStyles[select] || statusStyles.default
            }`}
          >
            <option value="">Select</option>
            <option value="appointment">Upcoming</option>
            <option value="completed">Completed</option>
            <option value="canceled">Canceled</option>
            <option value="waiting">Waiting</option>
            <option value="inqueue">In Queue</option>
            <option value="ongoing">Ongoing</option>
            <option value="noshow">No Show</option>
          </select>
        )}
      </td>
      <td className="px-4 xl:px-6 py-3 text-sm text-slate-700 dark:text-slate-200">
        {item?.purposeId?.purposeName || item?.bookTreatmentId?.name || "N/A"}
      </td>

      <td className="px-4 xl:px-6 py-3">
        <div className="flex gap-2 justify-center items-center">
          <button
            type="button"
            onClick={() => setDetailsOpen(true)}
            className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 flex items-center justify-center hover:bg-indigo-100 dark:hover:bg-indigo-900/60 active:bg-indigo-200 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
            title="View appointment details"
            aria-label="View appointment details"
          >
            <RemoveRedEyeIcon sx={{ fontSize: 18 }} />
          </button>
          {showPatientDetailsButton && item?.patientId?._id ? (
            <button
              type="button"
              onClick={handleViewPatient}
              className="w-8 h-8 rounded-lg bg-slate-50 dark:bg-slate-900/40 text-slate-700 dark:text-slate-200 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-900/60 active:bg-slate-200 transition-all focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-1"
              title="View patient"
              aria-label="View patient"
            >
              <PersonIcon sx={{ fontSize: 18 }} />
            </button>
          ) : null}
    
        </div>
        <AppointmentDetailsModal
          item={item}
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          onStatusChange={appointmStatus}
        />
        <PatientViewModal
          isOpen={patientModalOpen}
          onClose={closePatientModal}
          patientData={patientData}
          loading={patientLoading}
          theme={theme}
          onAddTreatment={
            item?.patientId?._id ? handleTreatmentClick : undefined
          }
        />
      </td>
    </tr>
  );
}

export default AppointmentTr;
